import React from 'react';
import { Box, List, ListItem, ListItemText, Typography } from '@material-ui/core';

/**
 * A styled container for summary sections with zebra-striped rows.
 * @param {{ title: string, children: React.ReactNode }} props
 */
function Section({ children, title }) {
    // Set the container background to the color of the *next* row, creating a fill effect.
    const containerBg = React.Children.count(children) % 2 === 0 ? 'bg-gray-100' : 'bg-gray-50';

    return (
        <div className="border rounded-lg overflow-hidden flex flex-col my-4">
            <h3 className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700">{title}</h3>
            <div className={`flex-grow ${containerBg}`}>
                <div className="[&>*:nth-child(odd)]:bg-gray-100 [&>*:nth-child(even)]:bg-gray-50">{children}</div>
            </div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="flex justify-between items-center py-1 px-3">
            <p className="text-xs text-gray-600">{label}</p>
            <p className="text-xs font-medium text-gray-900">{value}</p>
        </div>
    );
}

function ReceivingCorrectionPreview({ correctionData }) {
    return (
        <Box className="py-4">
            <Typography variant="h6">
                {correctionData.type === 'Preview' ? (
                    <span className="text-gray-500">Receiving Correction Preview</span>
                ) : (
                    <span className="text-green-500">Receiving Correction Results</span>
                )}
            </Typography>
            <Section title="Received Information">
                <InfoRow label="Number of Cases Received:" value={correctionData.numberOfCasesReceived} />
                <InfoRow label="Units per Case as Received:" value={correctionData.startingUnitsPerCase} />
                <InfoRow
                    label="Total Units Received:"
                    value={
                        correctionData.numberOfUnitsReceived +
                        correctionData.previousReceivingCorrectionAdjustments -
                        correctionData.previousReceivingCorrectionShorts
                    }
                />
                <InfoRow
                    label="Corrected total units (received cases × target UPC):"
                    value={correctionData.numberOfUnitsThatShouldHaveBeenReceived}
                />
            </Section>
            <Typography variant="body1" gutterBottom>
                Updating from {correctionData.startingUnitsPerCase} units per case to{' '}
                <strong>{correctionData.targetUnitsPerCase}</strong> units per case. Net adjustment:
                <strong>
                    {' '}
                    {correctionData.targetAdjustmentQuantity > 0 ? '+' : ''}
                    {correctionData.targetAdjustmentQuantity}
                </strong>{' '}
                units.
            </Typography>
            <Section title="Adjustment Details">
                <InfoRow
                    label="Net Adjustment:"
                    value={`${correctionData.targetAdjustmentQuantity > 0 ? '+' : ''}${correctionData.targetAdjustmentQuantity} units`}
                />
                <InfoRow
                    label="Units Adjusted in Existing Full Cases:"
                    value={`${correctionData.unitAdjustmentFromFullCaseUPCChange > 0 ? '+' : ''}${correctionData.unitAdjustmentFromFullCaseUPCChange} units (across ${correctionData.countFullCasesAdjusted} full cases)`}
                />
                <InfoRow
                    label="Units Decremented (From Replenishment Locations):"
                    value={`${correctionData.unitAdjustmentFromReplenishmentLocations} units`}
                />
                <InfoRow
                    label="Negative Variance Units:"
                    value={`${correctionData.negativeVarianceUnits > 0 ? '-' : ''}${correctionData.negativeVarianceUnits} units`}
                />
                <InfoRow
                    label="Full Cases Created (in Missing Location):"
                    value={`${correctionData.countFullCasesCreated} cases (${correctionData.fullCaseCreatedUnits > 0 ? '+' : ''}${correctionData.fullCaseCreatedUnits} units)`}
                />
                <InfoRow
                    label="Loose Units Created (in Missing Location):"
                    value={`${correctionData.countLooseUnitsCreated > 0 ? '+' : ''}${correctionData.countLooseUnitsCreated} units`}
                />
            </Section>
            {correctionData.type === 'Preview' && (
                <Typography variant="small" gutterBottom>
                    *Final results are calculated when confirmed and may differ from this preview.
                </Typography>
            )}
        </Box>
    );
}

export default ReceivingCorrectionPreview;
